import { c as _c } from "react/compiler-runtime";
import { COUNTRIES } from '@onefootprint/core';
import type { Iso3166TwoDigitCountryCode, Locale } from '@onefootprint/request-types';
import { cx } from 'class-variance-authority';
import type { ParseKeys } from 'i18next';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { type FieldValues, type Path, type PathValue, type RegisterOptions, useFormContext, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import mergeRefs from 'react-merge-refs';
import { useInputMask } from '../../../../hooks/use-input-mask';
import Select from '../../../select';
import TextInput from '../../../text-input';
import FormWrapper from '../../internals/form-wrapper';
import { extractNationalPart, formatToNational, getCallingCode, getCountriesWithCallingCode, getMask, isMaskLongestNumber, isPhoneNumberValid, parseDefaultValue, parsePastedNumber, resolveInputValue, toE164 } from '../../utils/tel-utils';
import type { FormInputProps } from '../form-input';
const cursorTrackingConfig = {
  delimiters: ['-', '(', ')']
};
export type FormTelProps<TFieldValues extends FieldValues> = {
  name: Path<TFieldValues>;
  rules?: RegisterOptions<TFieldValues, Path<TFieldValues>>;
  onChange?: (value: string) => void;
  locale?: Locale;
  defaultValue?: string;
  'data-auto-advance'?: boolean;
} & Omit<FormInputProps<TFieldValues>, 'name' | 'rules' | 'type' | 'autoComplete' | 'onChange' | 'placeholder' | 'defaultValue' | 'onSubmit'>;
export default function FormTel(t0) {
  const $ = _c(50);
  const {
    rules,
    name,
    label,
    hint,
    addon,
    errorPosition: t1,
    orientation: t2,
    size: t3,
    id: baseId,
    onChange,
    locale,
    defaultValue,
    "data-auto-advance": autoAdvance,
    ...props
  } = t0;
  const errorPosition = t1 === undefined ? "relative" : t1;
  const orientation = t2 === undefined ? "vertical" : t2;
  const size = t3 === undefined ? "default" : t3;
  const {
    t
  } = useTranslation("ui");
  const {
    register,
    setValue,
    getValues,
    control,
    getFieldState
  } = useFormContext();
  const formState = useFormState({
    control,
    name,
    disabled: props.disabled
  });
  let t4;
  if ($[0] !== formState || $[1] !== getFieldState || $[2] !== name) {
    t4 = getFieldState(name, formState);
    $[0] = formState;
    $[1] = getFieldState;
    $[2] = name;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const {
    error
  } = t4;
  const telInputRef = useRef(null);
  const lastE164Ref = useRef("");
  let t5;
  if ($[4] !== getValues || $[5] !== name) {
    t5 = getValues(name);
    $[4] = getValues;
    $[5] = name;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const rhfDefaultValue = t5;
  const t6 = defaultValue || rhfDefaultValue;
  let t7;
  if ($[7] !== locale || $[8] !== t6) {
    t7 = parseDefaultValue(t6, locale);
    $[7] = locale;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const parsedDefaultValue = t7;
  const contextId = useId();
  const [country, setCountry] = useState(parsedDefaultValue?.country ?? "US");
  const [isFocused, setIsFocused] = useState(false);
  const callingCode = getCallingCode(country) || "";
  const id = baseId || contextId;
  const {
    inputRef
  } = useInputMask(cursorTrackingConfig);
  let t8;
  if ($[10] !== country) {
    t8 = getMask(country);
    $[10] = country;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const placeholder = t8;
  let t9;
  if ($[12] !== t) {
    t9 = getCountriesWithCallingCode(COUNTRIES.map(option => ({
      ...option,
      label: t(`global.countries.${option.value}` as ParseKeys<'ui'>, {
        defaultValue: option.label
      })
    })));
    $[12] = t;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const countriesWithCallingCode = t9;
  let t10;
  bb0: {
    if (!parsedDefaultValue?.value) {
      t10 = "";
      break bb0;
    }
    let t11;
    if ($[14] !== parsedDefaultValue.country || $[15] !== parsedDefaultValue.value) {
      t11 = extractNationalPart(parsedDefaultValue.value, parsedDefaultValue.country);
      $[14] = parsedDefaultValue.country;
      $[15] = parsedDefaultValue.value;
      $[16] = t11;
    } else {
      t11 = $[16];
    }
    t10 = t11;
  }
  const nationalDefaultValue = t10;
  let t11;
  if ($[17] !== country || $[18] !== name || $[19] !== setValue) {
    t11 = e => {
      e.preventDefault();
      const nationalNumber = parsePastedNumber(e.clipboardData.getData("text"), country);
      setValue(name, nationalNumber as PathValue<TFieldValues, Path<TFieldValues>>, {
        shouldDirty: true,
        shouldValidate: true
      });
    };
    $[17] = country;
    $[18] = name;
    $[19] = setValue;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const handlePaste = t11;
  let t12;
  if ($[21] !== country || $[22] !== t) {
    t12 = value => {
      if (isPhoneNumberValid(value, country)) {
        return true;
      }
      return t("components.form.tel.errors.invalid");
    };
    $[21] = country;
    $[22] = t;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const telRules = {
    validate: t12,
    ...rules
  };
  const {
    ref: rhfRef,
    ...registerProps
  } = register(name, {
    ...telRules,
    disabled: formState.disabled || props.disabled,
    onChange: t13 => {
      const {
        target: t14
      } = t13;
      const {
        value: value_0
      } = t14;
      const e164Value = toE164(value_0, country, callingCode);
      lastE164Ref.current = e164Value;
      const formattedNational = formatToNational(e164Value, country, callingCode) as PathValue<TFieldValues, Path<TFieldValues>>;
      setValue(name, formattedNational);
    },
    setValueAs: value_1 => {
      if (!value_1?.trim()) {
        return "";
      }
      if (lastE164Ref.current) {
        const result = lastE164Ref.current;
        lastE164Ref.current = "";
        onChange?.(result);
        return result;
      }
      const e164Value_0 = toE164(value_1, country, callingCode);
      onChange?.(e164Value_0);
      return e164Value_0;
    }
  });
  let t15;
  if ($[24] !== locale) {
    t15 = [locale];
    $[24] = locale;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  useEffect(() => {
    if (telInputRef.current?.value) {
      const {
        nationalValue,
        country: resolvedCountry
      } = resolveInputValue(telInputRef.current.value, locale, callingCode);
      telInputRef.current.value = nationalValue;
      setCountry(resolvedCountry);
    }
  }, t15);
  const t16 = !isFocused;
  let t17;
  if ($[26] !== isFocused || $[27] !== t16) {
    t17 = cx("[&>button]:rounded-b-none!", {
      "[&>button]:border-b!": isFocused,
      "[&>button]:border-b-0!": t16
    });
    $[26] = isFocused;
    $[27] = t16;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  let t19;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => {
      setIsFocused(true);
    };
    t19 = () => {
      setIsFocused(false);
    };
    $[29] = t18;
    $[30] = t19;
  } else {
    t18 = $[29];
    t19 = $[30];
  }
  let t20;
  if ($[31] !== name || $[32] !== setValue) {
    t20 = value_2 => {
      const countryCode = value_2 as Iso3166TwoDigitCountryCode;
      setCountry(countryCode);
      setValue(name, "" as PathValue<TFieldValues, Path<TFieldValues>>, {
        shouldDirty: true
      });
    };
    $[31] = name;
    $[32] = setValue;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  const T0 = TextInput;
  const t21 = mergeRefs([telInputRef, rhfRef, inputRef]);
  const t22 = "tel";
  const t23 = "tel";
  const t24 = autoAdvance && isMaskLongestNumber(country) ? true : undefined;
  const t25 = !!error;
  const t26 = "mask";
  const t27 = `calc(${callingCode.length}ch + 1.25rem)`;
  let t28;
  if ($[34] !== t27) {
    t28 = {
      paddingLeft: t27
    };
    $[34] = t27;
    $[35] = t28;
  } else {
    t28 = $[35];
  }
  const t29 = cx("rounded-t-none!", {
    "rounded-bl-none! rounded-tl-none!": addon,
    "border-t-0": isFocused
  }, props.className);
  let t30;
  if ($[36] !== T0 || $[37] !== handlePaste || $[38] !== id || $[39] !== nationalDefaultValue || $[40] !== placeholder || $[41] !== props || $[42] !== registerProps || $[43] !== size || $[44] !== t21 || $[45] !== t24 || $[46] !== t25 || $[47] !== t28 || $[48] !== t29) {
    t30 = <T0 {...props} {...registerProps} ref={t21} onPaste={handlePaste} defaultValue={nationalDefaultValue} type={t22} autoComplete={t23} placeholder={placeholder} data-auto-advance={t24} hasError={t25} id={id} size={size} data-dd-privacy={t26} style={t28} className={t29} />;
    $[36] = T0;
    $[37] = handlePaste;
    $[38] = id;
    $[39] = nationalDefaultValue;
    $[40] = placeholder;
    $[41] = props;
    $[42] = registerProps;
    $[43] = size;
    $[44] = t21;
    $[45] = t24;
    $[46] = t25;
    $[47] = t28;
    $[48] = t29;
    $[49] = t30;
  } else {
    t30 = $[49];
  }
  return <div className="flex flex-col min-w-[300px]"><FormWrapper id={id} error={error?.message} label={label} errorPosition={errorPosition} size={size} hint={hint} addon={addon} orientation={orientation} fullWidth={true} disabled={formState.disabled || props.disabled}><div><Select disabled={formState.disabled || props.disabled} options={countriesWithCallingCode} className={t17} onFocus={t18} onBlur={t19} onChange={t20} value={country} /><div className="relative isolate"><span className={cx("absolute left-3 inset-y-0 flex items-center pointer-events-none select-none z-10 text-sm text-primary", {
            "text-tertiary": formState.disabled || props.disabled
          })} aria-hidden="true">{callingCode}</span>{t30}</div></div></FormWrapper></div>;
}